import {
  EMAIL_VERIFICATION_CATEGORY,
  QUESTIONNAIRE_CATEGORY,
  type WelcomePreviewStep,
} from '@/features/workflows/components/simple/preview-screens';

import type { SessionStepDto } from '../schemas/sessionSchemas';

/**
 * The overview heading for a step type that has no API binding to read one from.
 *
 * One place, so a new provider-less step is one entry rather than another `??` in the chain below —
 * which is how the second one would have been forgotten.
 */
function localCategoryFor(stepType: string): string | undefined {
  if (stepType === 'questionnaire') return QUESTIONNAIRE_CATEGORY;
  if (stepType === 'email_verification') return EMAIL_VERIFICATION_CATEGORY;
  return undefined;
}

/**
 * Session steps → rows for the welcome overview.
 *
 * Shared by the PRE-OTP screen (built from the provisional intro) and the live run,
 * so the applicant sees an identical list before and after proving their number.
 * Duplicating it is how the two would quietly diverge.
 *
 * The category is read off the step's FIRST-PRIORITY binding — the method the
 * applicant will actually get. It has to come from the binding because both screens
 * are anonymous and cannot call the authenticated catalogue endpoint.
 */
export function toWelcomeSteps(
  steps: SessionStepDto[],
  /**
   * Step id → a better name than the step's own `displayName`.
   *
   * Needed because `displayName` is FROZEN when the run starts. A questionnaire step created before
   * the builder began naming these carries the generic "Questionnaire", and no amount of fixing the
   * builder can change what is already stored on a live session — so the applicant's overview listed
   * "Questionnaire" three times while the editor's preview showed the three real names.
   *
   * Resolved from the questionnaires themselves by `useSessionQuestionnaireNames`, which works on the
   * anonymous shared-link route as well as the authenticated one.
   */
  names?: Map<string, string>,
): WelcomePreviewStep[] {
  return steps.map((s) => {
    const primary = s.apiBindings.slice().sort((a, b) => a.priority - b.priority)[0];
    return {
      id: s.id,
      stepType: s.stepType,
      // `displayName` — which the workflow builder sets to the attached questionnaire's name, so
      // three questionnaire steps read as their three questionnaires rather than as "Questionnaire"
      // three times.
      name: names?.get(s.id) ?? s.displayName,
      // Provider-less steps have no API binding, so they carry no category and would be grouped
      // under "Other Verification" — see QUESTIONNAIRE_CATEGORY and EMAIL_VERIFICATION_CATEGORY for
      // why that is wrong for each of them.
      category: primary?.category ?? localCategoryFor(s.stepType),
    };
  });
}

/**
 * Remembers that the applicant already saw the overview before the OTP gate, so the
 * live run does not open on the same screen a second time.
 *
 * sessionStorage keyed by the run's token: it survives a reload of the run (where
 * re-showing the overview would look like the flow restarted) but not a new tab or a
 * later visit, where showing it again is the friendlier default. Wrapped in try /
 * catch because storage throws outright in private-mode browsers — losing the flag
 * costs one extra screen, so it must never break the run.
 */
const introSeenKey = (publicToken: string) => `sc.intro.${publicToken}`;

export function markIntroSeen(publicToken: string) {
  if (!publicToken) return;
  try {
    sessionStorage.setItem(introSeenKey(publicToken), '1');
  } catch {
    // ignore — the run just opens on the overview once more
  }
}

export function wasIntroSeen(publicToken: string): boolean {
  if (!publicToken) return false;
  try {
    return sessionStorage.getItem(introSeenKey(publicToken)) === '1';
  } catch {
    return false;
  }
}
